import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  githubAuthApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import Alert from '@material-ui/lab/Alert';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { useBatchEditorApi } from './useBatchEditorApi';
import { BatchJobsPanel } from './BatchJobsPanel';

type RepoRef = {
  host: string;
  owner: string;
  repo: string;
};

type CatalogStatus = 'registered' | 'unregistered' | 'missing' | 'unknown';

type RepoInventoryItem = RepoRef & {
  fullName: string;
  defaultBranch: string;
  archived: boolean;
  private: boolean;
  htmlUrl: string;
  catalogEntityRef?: string;
  catalogOwner?: string;
  catalogLifecycle?: string;
  hasCatalogInfo: boolean;
  catalogStatus: CatalogStatus;
  warning?: 'missing-catalog';
};

type CatalogRegistrationResult = RepoRef & {
  outcome: 'registered' | 'already-registered' | 'no-catalog-info' | 'archived' | 'error';
  message?: string;
};

type CatalogUnregistrationResult = RepoRef & {
  outcome:
    | 'unregistered'
    | 'not-registered'
    | 'no-removable-location'
    | 'not-archived'
    | 'error';
  message?: string;
};

type Soc2Result = RepoRef & {
  lifecycle?: string;
  current: 'true' | 'false' | 'unassigned';
  desired: 'true' | 'unassigned';
  compliant: boolean;
};

type Soc2ReconcileResult = RepoRef & {
  outcome: 'updated' | 'already-compliant' | 'archived' | 'error';
  from?: string;
  to?: string;
  message?: string;
};

const catalogStatusLabels: Record<CatalogStatus, string> = {
  registered: 'Registered',
  unregistered: 'Has catalog-info (not subscribed)',
  missing: 'No catalog-info',
  unknown: 'Unknown (not checked)',
};

type ScanResult = RepoInventoryItem & {
  path: string;
  status: 'present' | 'missing' | 'archived' | 'error';
  content?: string;
  contentHash?: string;
  normalizedHash?: string;
  error?: string;
};

type JobDetails = {
  id: string;
  status: string;
  workflowState?: 'draft' | 'pending-approval' | 'pending-merge' | 'warning' | 'complete' | 'error' | 'canceled';
  workflowBusy?: 'approval' | 'merge';
  approvedBy?: string;
  mergedBy?: string;
  title: string;
  operation: Operation;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  request?: {
    title?: string;
    body?: string;
    draft?: boolean;
  };
  totals: {
    queued: number;
    running: number;
    skipped: number;
    prOpened: number;
    manualReview: number;
    failed: number;
    merged: number;
  };
  targets: Array<RepoRef & {
    id: string;
    status: string;
    branchName?: string;
    prUrl?: string;
    errorMessage?: string;
  }>;
};

type Operation = 'create-missing-file' | 'edit-existing-file' | 'edit-catalog-info';

const operationLabels: Record<Operation, string> = {
  'create-missing-file': 'Create Missing File',
  'edit-existing-file': 'Edit Existing File',
  'edit-catalog-info': 'Edit catalog-info.yaml',
};

function repoKey(repo: RepoRef): string {
  // Lower-cased so it matches keys written from API results regardless of the
  // original host/owner/repo casing.
  return `${repo.host}/${repo.owner}/${repo.repo}`.toLowerCase();
}

function statusChip(status: string) {
  const color = status === 'pr-opened' || status === 'present' ? 'primary' : 'default';
  return <Chip size="small" color={color} label={status} />;
}

function groupCount(groups: Record<string, string[]> | undefined): string {
  if (!groups) return '0 groups';
  const values = Object.values(groups).filter(group => group.length > 0);
  return `${values.length} groups`;
}

type ScanGroups = { exact: Record<string, string[]>; normalized: Record<string, string[]> };

// Module-level caches persist across route changes within the SPA session, so
// navigating away and back to the page does not re-run the expensive multi-org
// discovery or lose in-progress work. They are intentionally not React state.
const REPOS_TTL_MS = 5 * 60 * 1000;
let reposCache: { url: string; data: RepoInventoryItem[]; ts: number } | undefined;

function jobIdFromSearch(search: string): string | undefined {
  const params = new URLSearchParams(search);
  if (!params.has('jobId')) {
    return undefined;
  }
  return params.get('jobId')?.trim() || undefined;
}

function urlHasJobIdParam(search: string): boolean {
  return new URLSearchParams(search).has('jobId');
}

function buildBatchEditorSearch(currentSearch: string, jobId: string | undefined): string {
  const params = new URLSearchParams(currentSearch);
  if (jobId) {
    params.set('jobId', jobId);
  } else {
    params.delete('jobId');
  }
  const next = params.toString();
  return next ? `?${next}` : '';
}

const sessionState: {
  owner?: string;
  selected: string[];
  catalogStatusOverrides: Record<string, CatalogStatus>;
  scanResults: ScanResult[];
  scanGroups: ScanGroups;
  lastScanSignature?: string;
  soc2Results: Soc2Result[];
  lastSoc2Signature?: string;
  currentJobId?: string;
  operation: Operation;
  path: string;
  content: string;
} = {
  owner: undefined,
  selected: [],
  catalogStatusOverrides: {},
  scanResults: [],
  scanGroups: { exact: {}, normalized: {} },
  soc2Results: [],
  lastSoc2Signature: undefined,
  currentJobId: undefined,
  operation: 'create-missing-file',
  path: 'catalog-info.yaml',
  content: '',
};

export function BatchEditorPage() {
  const discoveryApi = useApi(discoveryApiRef);
  const githubAuth = useApi(githubAuthApiRef);
  const identityApi = useApi(identityApiRef);
  const api = useBatchEditorApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [catalogFilter, setCatalogFilter] = useState<
    'all' | 'registered' | 'unregistered' | 'missing'
  >('all');
  const [refreshTick, setRefreshTick] = useState(0);
  const [catalogStatusOverrides, setCatalogStatusOverrides] = useState<
    Record<string, CatalogStatus>
  >(() => sessionState.catalogStatusOverrides);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [soc2Results, setSoc2Results] = useState<Soc2Result[]>(() => sessionState.soc2Results);
  const [lastSoc2Signature, setLastSoc2Signature] = useState<string | undefined>(
    () => sessionState.lastSoc2Signature,
  );
  const [soc2Busy, setSoc2Busy] = useState(false);
  const [soc2OnlyFlagged, setSoc2OnlyFlagged] = useState(true);
  const [notice, setNotice] = useState<string>();
  const [selected, setSelected] = useState<string[]>(() => sessionState.selected);
  const [operation, setOperation] = useState<Operation>(() => sessionState.operation);
  const [path, setPath] = useState(() => sessionState.path);
  const [content, setContent] = useState(() => sessionState.content);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [reviewers, setReviewers] = useState('');
  const [labels, setLabels] = useState('');
  const [draft, setDraft] = useState(false);
  const [overwriteDiffering, setOverwriteDiffering] = useState(false);
  const [scanResults, setScanResults] = useState<ScanResult[]>(() => sessionState.scanResults);
  const [scanGroups, setScanGroups] = useState<ScanGroups>(() => sessionState.scanGroups);
  const [lastScanSignature, setLastScanSignature] = useState<string | undefined>(
    () => sessionState.lastScanSignature,
  );
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmUnsubscribeOpen, setConfirmUnsubscribeOpen] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | undefined>(
    () => jobIdFromSearch(location.search) ?? sessionState.currentJobId,
  );
  const [submitting, setSubmitting] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [jobsPollTick, setJobsPollTick] = useState(0);
  const [error, setError] = useState<string>();

  const urlHadJobIdParam = useRef(urlHasJobIdParam(location.search));

  const selectCurrentJob = useCallback(
    (jobId: string | undefined) => {
      setCurrentJobId(jobId);
      sessionState.currentJobId = jobId;
      const nextSearch = buildBatchEditorSearch(location.search, jobId);
      if (nextSearch !== location.search) {
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      }
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasJobIdParam = params.has('jobId');
    const fromUrl = hasJobIdParam ? params.get('jobId')?.trim() || undefined : undefined;

    if (hasJobIdParam) {
      setCurrentJobId(prev => {
        if (prev === fromUrl) {
          return prev;
        }
        sessionState.currentJobId = fromUrl;
        return fromUrl;
      });
    } else if (urlHadJobIdParam.current) {
      setCurrentJobId(prev => {
        if (prev === undefined) {
          return prev;
        }
        sessionState.currentJobId = undefined;
        return undefined;
      });
    }

    urlHadJobIdParam.current = hasJobIdParam;
  }, [location.search]);

  const backend = useAsync(async () => discoveryApi.getBaseUrl('batch-editor'), [discoveryApi]);

  const currentUser = useAsync(
    async () => (await identityApi.getBackstageIdentity()).userEntityRef,
    [identityApi],
  );

  // The session cache is module-level and shared across mounts; if a different
  // user opens the page, clear any inherited in-progress work so one admin's
  // selection/scan/job state never leaks to another.
  useEffect(() => {
    const ref = currentUser.value;
    if (!ref) {
      return;
    }
    if (sessionState.owner && sessionState.owner !== ref) {
      setSelected([]);
      setCatalogStatusOverrides({});
      setScanResults([]);
      setScanGroups({ exact: {}, normalized: {} });
      setLastScanSignature(undefined);
      setSoc2Results([]);
      setLastSoc2Signature(undefined);
      urlHadJobIdParam.current = false;
      selectCurrentJob(undefined);
      setContent('');
      setOperation('create-missing-file');
      setPath('catalog-info.yaml');
      // Drop the cached repo inventory too, and force a fresh fetch, so a new
      // user never sees the previous user's discovered repositories.
      reposCache = undefined;
      setRefreshTick(value => value + 1);
    }
    sessionState.owner = ref;
  }, [currentUser.value, selectCurrentJob]);

  const reposState = useAsync(async () => {
    if (!backend.value) return [];
    // Reuse the session cache across remounts (e.g. switching sidebar tabs) so
    // returning to the page does not re-run discovery. "Refresh repos" clears
    // the cache to force a fresh fetch.
    if (
      reposCache &&
      reposCache.url === backend.value &&
      Date.now() - reposCache.ts < REPOS_TTL_MS
    ) {
      return reposCache.data;
    }
    // Always fetch the full inventory (including archived) once; the archived
    // toggle is then a cheap client-side filter rather than a full re-discovery.
    const data = await api.getJson<{ repos: RepoInventoryItem[] }>(
      `${backend.value}/repos?includeArchived=true`,
    );
    const repos = data.repos as RepoInventoryItem[];
    reposCache = { url: backend.value, data: repos, ts: Date.now() };
    return repos;
  }, [api, backend.value, refreshTick]);

  const currentJobState = useAsync(async () => {
    if (!backend.value || !currentJobId) return undefined;
    const url =
      pollTick > 0
        ? `${backend.value}/jobs/${currentJobId}?reconcile=true`
        : `${backend.value}/jobs/${currentJobId}`;
    const data = await api.getJson<{ job: JobDetails }>(url);
    return data.job as JobDetails;
  }, [api, backend.value, currentJobId, pollTick]);

  const currentJobStatus = currentJobState.value?.status;
  const currentJobWorkflowState = currentJobState.value?.workflowState;
  const currentJobWorkflowBusy = currentJobState.value?.workflowBusy;
  const shouldPollCurrentJob =
    currentJobStatus === 'queued' ||
    currentJobStatus === 'running' ||
    currentJobWorkflowState === 'pending-approval' ||
    currentJobWorkflowState === 'pending-merge' ||
    currentJobWorkflowState === 'warning' ||
    Boolean(currentJobWorkflowBusy);
  const jobMissing = useMemo(() => {
    const message = currentJobState.error?.message ?? '';
    return /\b404\b/.test(message) || /not found/i.test(message);
  }, [currentJobState.error]);
  const displayedJob = jobMissing ? undefined : currentJobState.value;

  const jobsState = useAsync(async () => {
    if (!backend.value) return [];
    const url =
      jobsPollTick > 0
        ? `${backend.value}/jobs?reconcile=true`
        : `${backend.value}/jobs`;
    const data = await api.getJson<{ jobs: JobDetails[] }>(url);
    return data.jobs as JobDetails[];
  }, [api, backend.value, jobsPollTick, pollTick, currentJobStatus]);

  useEffect(() => {
    const jobs = jobsState.value ?? [];
    const hasActiveJobs = jobs.some(
      job =>
        job.status === 'queued' ||
        job.status === 'running' ||
        Boolean(job.workflowBusy) ||
        job.workflowState === 'pending-approval' ||
        job.workflowState === 'pending-merge' ||
        job.workflowState === 'warning',
    );
    if (!hasActiveJobs) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      setJobsPollTick(value => value + 1);
    }, 5000);
    return () => window.clearInterval(handle);
  }, [jobsState.value]);

  useEffect(() => {
    if (!currentJobId) return undefined;
    if (jobMissing) return undefined;
    if (!shouldPollCurrentJob) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      setPollTick(value => value + 1);
    }, 5000);
    return () => window.clearInterval(handle);
  }, [currentJobId, jobMissing, shouldPollCurrentJob]);

  useEffect(() => {
    if (!jobMissing || !currentJobId) {
      return;
    }
    selectCurrentJob(undefined);
  }, [jobMissing, currentJobId, selectCurrentJob]);

  useEffect(() => {
    if (operation === 'edit-catalog-info') {
      setPath('catalog-info.yaml');
    }
  }, [operation]);

  // Persist in-progress work into the module-level session store so it survives
  // route remounts (switching tabs) without a refetch or re-scan.
  useEffect(() => {
    sessionState.selected = selected;
    sessionState.catalogStatusOverrides = catalogStatusOverrides;
    sessionState.scanResults = scanResults;
    sessionState.scanGroups = scanGroups;
    sessionState.lastScanSignature = lastScanSignature;
    sessionState.soc2Results = soc2Results;
    sessionState.lastSoc2Signature = lastSoc2Signature;
    sessionState.currentJobId = currentJobId;
    sessionState.operation = operation;
    sessionState.path = path;
    sessionState.content = content;
  }, [
    selected,
    catalogStatusOverrides,
    scanResults,
    scanGroups,
    lastScanSignature,
    soc2Results,
    lastSoc2Signature,
    currentJobId,
    operation,
    path,
    content,
  ]);

  const repos = useMemo(() => reposState.value ?? [], [reposState.value]);
  const orgs = useMemo(
    () => Array.from(new Set(repos.map(repo => repo.owner))).sort((a, b) => a.localeCompare(b)),
    [repos],
  );
  const activeStep = useMemo(() => {
    if (currentJobId) return 4;
    if (scanResults.length) return 2;
    if (selected.length) return 1;
    return 0;
  }, [currentJobId, scanResults.length, selected.length]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const effectiveStatus = useCallback(
    (repo: RepoInventoryItem): CatalogStatus =>
      catalogStatusOverrides[repoKey(repo)] ?? repo.catalogStatus,
    [catalogStatusOverrides],
  );

  const filteredRepos = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return repos.filter(repo => {
      const matchesSearch =
        !lowerSearch ||
        repo.fullName.toLowerCase().includes(lowerSearch) ||
        repo.owner.toLowerCase().includes(lowerSearch) ||
        repo.repo.toLowerCase().includes(lowerSearch);
      const matchesArchived = includeArchived || !repo.archived;
      const matchesOrg = orgFilter === 'all' || repo.owner === orgFilter;
      const status = catalogStatusOverrides[repoKey(repo)] ?? repo.catalogStatus;
      const matchesCatalog =
        catalogFilter === 'all' ||
        (catalogFilter === 'registered' && status === 'registered') ||
        (catalogFilter === 'unregistered' && status === 'unregistered') ||
        (catalogFilter === 'missing' && status === 'missing');
      return matchesSearch && matchesArchived && matchesOrg && matchesCatalog;
    });
  }, [catalogFilter, catalogStatusOverrides, includeArchived, orgFilter, repos, search]);

  const selectedRepos = useMemo(
    () => repos.filter(repo => selectedSet.has(repoKey(repo))),
    [repos, selectedSet],
  );
  const selectedArchivedRepos = useMemo(
    () => selectedRepos.filter(repo => repo.archived),
    [selectedRepos],
  );

  const scanContextSignature = useMemo(
    () => JSON.stringify({ operation, path, selected: [...selected].sort() }),
    [operation, path, selected],
  );
  const scanIsCurrent =
    scanResults.length > 0 && lastScanSignature === scanContextSignature;

  const soc2ContextSignature = useMemo(
    () => JSON.stringify([...selected].sort()),
    [selected],
  );
  const soc2IsCurrent =
    soc2Results.length > 0 && lastSoc2Signature === soc2ContextSignature;
  const displayedSoc2Results = useMemo(() => {
    if (!soc2IsCurrent) {
      return [];
    }
    return soc2Results.filter(result => selectedSet.has(repoKey(result)));
  }, [soc2IsCurrent, soc2Results, selectedSet]);

  const eligibleScanResults = useMemo(() => {
    if (!scanIsCurrent) {
      return [];
    }
    const byStatus =
      operation === 'create-missing-file'
        ? scanResults.filter(result => result.status === 'missing')
        : scanResults.filter(result => result.status === 'present');
    // Archived repos are skipped by the job API; never count them as PR targets.
    return byStatus.filter(
      result => selectedSet.has(repoKey(result)) && !result.archived,
    );
  }, [operation, scanIsCurrent, scanResults, selectedSet]);

  const operationWorkflowHelp = useMemo(() => {
    if (operation === 'create-missing-file') {
      return '1) Scan to see which selected repos are missing the file. 2) Compose the file. 3) Open PRs only where it is absent.';
    }
    if (operation === 'edit-existing-file') {
      return 'Scan to see which selected repos already have the file, then edit and open PRs.';
    }
    return 'Scan selected repos, edit catalog-info.yaml, then open PRs.';
  }, [operation]);

  const scanButtonLabel =
    operation === 'create-missing-file' ? 'Scan for missing file' : 'Scan selected repos';

  const eligibleTargetsHelp = useMemo(() => {
    if (!scanIsCurrent) {
      return 'Run a fresh scan for the current selection and operation before submitting.';
    }
    if (operation === 'create-missing-file') {
      return 'Only selected repositories missing the file will be targeted.';
    }
    return 'Only selected repositories where the file exists will be targeted.';
  }, [operation, scanIsCurrent]);

  const baseline = useMemo(() => {
    const present = scanResults.find(result => result.status === 'present' && result.content);
    return present?.content;
  }, [scanResults]);

  const exactGroups = Object.values(scanGroups.exact).filter(group => group.length > 0);
  const normalizedGroups = Object.values(scanGroups.normalized).filter(group => group.length > 0);

  const repoColumns: TableColumn<RepoInventoryItem>[] = useMemo(
    () => [
    {
      title: '',
      field: 'selected',
      render: repo => (
        <Checkbox
          checked={selectedSet.has(repoKey(repo))}
          onChange={event => {
            const key = repoKey(repo);
            setSelected(current =>
              event.target.checked ? [...current, key] : current.filter(item => item !== key),
            );
          }}
        />
      ),
    },
    { title: 'Org', field: 'owner' },
    {
      title: 'Repository',
      field: 'fullName',
      render: repo => {
        const status = effectiveStatus(repo);
        return (
          <>
            <Tooltip title="Open on GitHub in a new tab">
              <Link href={repo.htmlUrl} target="_blank" rel="noopener noreferrer">
                {repo.fullName}
              </Link>
            </Tooltip>{' '}
            {status !== 'registered' && (
              <Tooltip title={catalogStatusLabels[status]}>
                <WarningIcon fontSize="small" color="secondary" />
              </Tooltip>
            )}
          </>
        );
      },
    },
    { title: 'Default Branch', field: 'defaultBranch' },
    {
      title: 'Catalog',
      field: 'catalogStatus',
      render: repo => {
        const status = effectiveStatus(repo);
        if (status === 'registered') {
          return (
            <Tooltip title={repo.catalogEntityRef ?? 'Registered in Backstage'}>
              <Chip size="small" color="primary" label="registered" />
            </Tooltip>
          );
        }
        if (status === 'unregistered') {
          return (
            <Tooltip title="catalog-info.yaml exists but the location is not subscribed in Backstage">
              <Chip size="small" label="not subscribed" />
            </Tooltip>
          );
        }
        if (status === 'missing') {
          return <Chip size="small" label="no catalog-info" />;
        }
        return <Chip size="small" variant="outlined" label="not checked" />;
      },
    },
    {
      title: 'Archived',
      field: 'archived',
      render: repo => (repo.archived ? <Chip size="small" label="archived" /> : null),
    },
    ],
    [selectedSet, effectiveStatus],
  );

  const targetColumns: TableColumn<JobDetails['targets'][number]>[] = [
    { title: 'Repository', render: row => `${row.owner}/${row.repo}` },
    { title: 'Status', field: 'status', render: row => statusChip(row.status) },
    { title: 'Branch', field: 'branchName' },
    {
      title: 'PR',
      field: 'prUrl',
      render: row =>
        row.prUrl ? (
          <Link href={row.prUrl} target="_blank" rel="noopener noreferrer">
            Open PR
          </Link>
        ) : null,
    },
    { title: 'Message', field: 'errorMessage' },
  ];

  const checkCatalogStatus = useCallback(async () => {
    if (!backend.value || !selectedRepos.length) return;
    setCatalogBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const data = await api.postJson<{
        results: Array<RepoRef & { catalogStatus: CatalogStatus; rateLimited?: boolean }>;
      }>(`${backend.value}/catalog/status`, {
          repositories: selectedRepos.map(({ host, owner, repo, defaultBranch }) => ({
            host,
            owner,
            repo,
            defaultBranch,
          })),
      });
      const results = data.results;
      setCatalogStatusOverrides(current => {
        const next = { ...current };
        for (const result of results) {
          next[repoKey(result)] = result.catalogStatus;
        }
        return next;
      });
      const rateLimited = results.filter(result => result.rateLimited).length;
      if (rateLimited) {
        setNotice(
          `GitHub rate limit reached: ${rateLimited} repo(s) could not be checked. ` +
            `Results are cached for 10 minutes; wait for the limit to reset, narrow your selection, then re-check the remaining repos.`,
        );
      }
    } catch (statusError) {
      setError(statusError instanceof Error ? statusError.message : String(statusError));
    } finally {
      setCatalogBusy(false);
    }
  }, [api, backend.value, selectedRepos]);

  const registerCatalogLocations = useCallback(async () => {
    if (!backend.value || !selectedRepos.length) return;
    setCatalogBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const data = await api.postJson<{ results: CatalogRegistrationResult[] }>(
        `${backend.value}/catalog/register`,
        {
          repositories: selectedRepos.map(({ host, owner, repo, defaultBranch, archived }) => ({
            host,
            owner,
            repo,
            defaultBranch,
            archived,
          })),
        },
      );
      const results = data.results;
      const counts = results.reduce<Record<string, number>>((acc, result) => {
        acc[result.outcome] = (acc[result.outcome] ?? 0) + 1;
        return acc;
      }, {});
      setCatalogStatusOverrides(current => {
        const next = { ...current };
        for (const result of results) {
          if (result.outcome === 'registered' || result.outcome === 'already-registered') {
            next[repoKey(result)] = 'registered';
          } else if (result.outcome === 'no-catalog-info') {
            next[repoKey(result)] = 'missing';
          }
        }
        return next;
      });
      setNotice(
        `Catalog registration: ${Object.entries(counts)
          .map(([outcome, count]) => `${count} ${outcome}`)
          .join(', ')}. Newly registered locations may take a moment to ingest.`,
      );
    } catch (registerError) {
      setError(registerError instanceof Error ? registerError.message : String(registerError));
    } finally {
      setCatalogBusy(false);
    }
  }, [api, backend.value, selectedRepos]);
  const unregisterArchivedCatalogLocations = useCallback(async () => {
    if (!backend.value || !selectedArchivedRepos.length) return;
    setCatalogBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const data = await api.postJson<{ results: CatalogUnregistrationResult[] }>(
        `${backend.value}/catalog/unregister-archived`,
        {
          repositories: selectedArchivedRepos.map(({ host, owner, repo, defaultBranch, archived }) => ({
            host,
            owner,
            repo,
            defaultBranch,
            archived,
          })),
        },
      );
      const results = data.results;
      const counts = results.reduce<Record<string, number>>((acc, result) => {
        acc[result.outcome] = (acc[result.outcome] ?? 0) + 1;
        return acc;
      }, {});
      setCatalogStatusOverrides(current => {
        const next = { ...current };
        for (const result of results) {
          if (result.outcome === 'unregistered') {
            next[repoKey(result)] = 'unregistered';
          }
        }
        return next;
      });
      setNotice(
        `Catalog cleanup: ${Object.entries(counts)
          .map(([outcome, count]) => `${count} ${outcome}`)
          .join(', ')}.`,
      );
    } catch (unregisterError) {
      setError(unregisterError instanceof Error ? unregisterError.message : String(unregisterError));
    } finally {
      setCatalogBusy(false);
    }
  }, [api, backend.value, selectedArchivedRepos]);

  const loadSoc2Results = useCallback(async (): Promise<Soc2Result[]> => {
    if (!backend.value || !selectedRepos.length) {
      return [];
    }
    const data = await api.postJson<{ results: Soc2Result[] }>(`${backend.value}/soc2/scan`, {
        repositories: selectedRepos.map(({ host, owner, repo, archived }) => ({
          host,
          owner,
          repo,
          archived,
        })),
    });
    const results = data.results;
    setSoc2Results(results);
    setLastSoc2Signature(soc2ContextSignature);
    return results;
  }, [api, backend.value, selectedRepos, soc2ContextSignature]);

  const checkSoc2 = useCallback(async () => {
    if (!backend.value || !selectedRepos.length) return;
    setSoc2Busy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const results = await loadSoc2Results();
      const flagged = results.filter(result => !result.compliant).length;
      setNotice(`SOC2 check: ${flagged} of ${results.length} selected repos are non-compliant.`);
    } catch (soc2Error) {
      setSoc2Results([]);
      setLastSoc2Signature(undefined);
      setError(soc2Error instanceof Error ? soc2Error.message : String(soc2Error));
    } finally {
      setSoc2Busy(false);
    }
  }, [backend.value, loadSoc2Results, selectedRepos.length]);

  const fixSoc2 = useCallback(async () => {
    if (!backend.value || !selectedRepos.length) return;
    setSoc2Busy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const data = await api.postJson<{ results: Soc2ReconcileResult[] }>(
        `${backend.value}/soc2/reconcile`,
        {
          repositories: selectedRepos.map(({ host, owner, repo, archived }) => ({
            host,
            owner,
            repo,
            archived,
          })),
        },
      );
      const results = data.results;
      const counts = results.reduce<Record<string, number>>((acc, result) => {
        acc[result.outcome] = (acc[result.outcome] ?? 0) + 1;
        return acc;
      }, {});
      await loadSoc2Results();
      setNotice(
        `SOC2 reconcile: ${Object.entries(counts)
          .map(([outcome, count]) => `${count} ${outcome}`)
          .join(', ')}.`,
      );
    } catch (soc2Error) {
      setError(soc2Error instanceof Error ? soc2Error.message : String(soc2Error));
    } finally {
      setSoc2Busy(false);
    }
  }, [api, backend.value, loadSoc2Results, selectedRepos]);

  const runScan = useCallback(async () => {
    if (!backend.value) return;
    setError(undefined);
    try {
      const githubToken = await githubAuth.getAccessToken(['repo', 'read:user', 'user:email']);
      const data = await api.postJson<{ results: ScanResult[]; groups: ScanGroups }>(
        `${backend.value}/scan`,
        {
          repositories: selectedRepos.map(({ host, owner, repo }) => ({ host, owner, repo })),
          path,
          includeArchived,
        },
        {
          headers: { 'X-GitHub-Authorization': `Bearer ${githubToken}` },
        },
      );
      setScanResults(data.results);
      setScanGroups(data.groups);
      setLastScanSignature(scanContextSignature);
      const selectedKeys = new Set(selectedRepos.map(repo => repoKey(repo)));
      const firstPresent = data.results.find(
        (result: ScanResult) => result.status === 'present' && result.content,
      );
      const missingForSelection = data.results.filter(
        (result: ScanResult) =>
          result.status === 'missing' && selectedKeys.has(repoKey(result)),
      );
      const nextContent =
        operation === 'create-missing-file' ? '' : (firstPresent?.content ?? '');
      setContent(nextContent);
      if (operation === 'create-missing-file' && missingForSelection.length > 0) {
        setEditorOpen(true);
      } else if (firstPresent?.content) {
        setEditorOpen(true);
      }
    } catch (scanError) {
      setScanResults([]);
      setScanGroups({ exact: {}, normalized: {} });
      setLastScanSignature(undefined);
      throw scanError;
    }
  }, [
    backend.value,
    api,
    githubAuth,
    includeArchived,
    operation,
    path,
    scanContextSignature,
    selectedRepos,
  ]);

  const submitJob = useCallback(async () => {
    if (!backend.value) return;
    setSubmitting(true);
    setError(undefined);
    try {
      // Author PRs as the signed-in user (not the GitHub App) so they cannot
      // approve their own PRs. This prompts a GitHub authorization the first time.
      const githubToken = await githubAuth.getAccessToken(['repo', 'read:user', 'user:email']);
      const targets = eligibleScanResults.map(({ host, owner, repo }) => ({ host, owner, repo }));
      const data = await api.postJson<{ job: JobDetails }>(
        `${backend.value}/jobs`,
        {
          operation,
          repositories: targets,
          path,
          content,
          expectedContent: baseline,
          overwriteDiffering,
          title: title || `${operationLabels[operation]}: ${path}`,
          body,
          reviewers: reviewers
            .split(',')
            .map(value => value.trim())
            .filter(Boolean),
          labels: labels
            .split(',')
            .map(value => value.trim())
            .filter(Boolean),
          draft,
        },
        {
          headers: { 'X-GitHub-Authorization': `Bearer ${githubToken}` },
        },
      );
      selectCurrentJob(data.job.id);
      setEditorOpen(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setSubmitting(false);
    }
  }, [
    backend.value,
    baseline,
    body,
    content,
    draft,
    eligibleScanResults,
    api,
    githubAuth,
    labels,
    operation,
    overwriteDiffering,
    path,
    reviewers,
    selectCurrentJob,
    title,
  ]);

  const scanSummary = useMemo(() => {
    const counts = scanResults.reduce<Record<string, number>>((acc, result) => {
      acc[result.status] = (acc[result.status] ?? 0) + 1;
      return acc;
    }, {});
    return counts;
  }, [scanResults]);

  return (
    <Page themeId="tool">
      <Header
        title="Batch Repository Editor"
        subtitle="Scan repositories, make safe PR-only edits, and keep an audit trail."
      />
      <Content>
        {(reposState.loading || backend.loading) && <Progress />}
        {(error || reposState.error || jobsState.error || (currentJobState.error && !jobMissing)) && (
          <Box mb={2}>
            <Alert severity="error">
              {error ||
                reposState.error?.message ||
                jobsState.error?.message ||
                currentJobState.error?.message}
            </Alert>
          </Box>
        )}
        {jobMissing && (
          <Box mb={2}>
            <Alert severity="warning">Selected batch job was not found and has been cleared.</Alert>
          </Box>
        )}
        {notice && (
          <Box mb={2}>
            <Alert severity="info" onClose={() => setNotice(undefined)}>
              {notice}
            </Alert>
          </Box>
        )}

        <Stepper activeStep={activeStep}>
          <Step>
            <StepLabel>Select Repositories</StepLabel>
          </Step>
          <Step>
            <StepLabel>Choose Operation</StepLabel>
          </Step>
          <Step>
            <StepLabel>Scan and Group</StepLabel>
          </Step>
          <Step>
            <StepLabel>Edit and Confirm</StepLabel>
          </Step>
          <Step>
            <StepLabel>Track PRs</StepLabel>
          </Step>
        </Stepper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <InfoCard title="Repository Fleet">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search repositories"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Org</InputLabel>
                    <Select
                      value={orgFilter}
                      onChange={event => setOrgFilter(event.target.value as string)}
                    >
                      <MenuItem value="all">All orgs</MenuItem>
                      {orgs.map(org => (
                        <MenuItem key={org} value={org}>
                          {org}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Catalog</InputLabel>
                    <Select
                      value={catalogFilter}
                      onChange={event => setCatalogFilter(event.target.value as typeof catalogFilter)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="registered">Registered</MenuItem>
                      <MenuItem value="unregistered">Not subscribed</MenuItem>
                      <MenuItem value="missing">No catalog-info</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeArchived}
                        onChange={event => setIncludeArchived(event.target.checked)}
                      />
                    }
                    label="Archived"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      reposCache = undefined;
                      setRefreshTick(value => value + 1);
                    }}
                    disabled={reposState.loading}
                  >
                    Refresh repos
                  </Button>{' '}
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() =>
                      setSelected(current =>
                        Array.from(new Set([...current, ...filteredRepos.map(repoKey)])),
                      )
                    }
                  >
                    Select all matching filter ({filteredRepos.length})
                  </Button>{' '}
                  <Button variant="outlined" onClick={() => setSelected([])}>
                    Clear
                  </Button>{' '}
                  <Typography component="span" color="textSecondary">
                    {repos.length} discovered · {filteredRepos.length} matching · {selected.length}{' '}
                    selected
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    disabled={!selectedRepos.length || catalogBusy}
                    onClick={() => {
                      checkCatalogStatus();
                    }}
                  >
                    Check catalog status ({selectedRepos.length})
                  </Button>{' '}
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!selectedRepos.length || catalogBusy}
                    onClick={() => {
                      registerCatalogLocations();
                    }}
                  >
                    Subscribe catalog locations ({selectedRepos.length})
                  </Button>{' '}
                  <Button
                    variant="outlined"
                    disabled={!selectedArchivedRepos.length || catalogBusy}
                    onClick={() => {
                      setConfirmUnsubscribeOpen(true);
                    }}
                  >
                    Unsubscribe archived repos ({selectedArchivedRepos.length})
                  </Button>{' '}
                  <Typography component="span" color="textSecondary">
                    Subscribe selected repos with catalog-info.yaml, or unsubscribe selected
                    archived repos to keep the catalog clean.
                  </Typography>
                </Grid>
              </Grid>
              <Table
                options={{ paging: true, pageSize: 10, search: false }}
                columns={repoColumns}
                data={filteredRepos}
              />
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <InfoCard title="Operation">
              <FormControl fullWidth margin="normal">
                <InputLabel>Operation</InputLabel>
                <Select value={operation} onChange={event => setOperation(event.target.value as Operation)}>
                  <MenuItem value="create-missing-file">Create missing file</MenuItem>
                  <MenuItem value="edit-existing-file">Edit existing file</MenuItem>
                  <MenuItem value="edit-catalog-info">Edit catalog-info.yaml</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                margin="normal"
                label="File path"
                value={path}
                disabled={operation === 'edit-catalog-info'}
                onChange={event => setPath(event.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="PR title"
                value={title}
                placeholder={`${operationLabels[operation]}: ${path}`}
                onChange={event => setTitle(event.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="PR body"
                multiline
                minRows={3}
                value={body}
                onChange={event => setBody(event.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Reviewers"
                helperText="Comma-separated GitHub usernames"
                value={reviewers}
                onChange={event => setReviewers(event.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Labels"
                helperText="Comma-separated GitHub labels"
                value={labels}
                onChange={event => setLabels(event.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={draft} onChange={event => setDraft(event.target.checked)} />}
                label="Open draft PRs"
              />
              <Typography color="textSecondary" variant="body2">
                {operationWorkflowHelp}
              </Typography>
              <Box mt={2}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={!selectedRepos.length || !path}
                  onClick={() => {
                    runScan().catch(scanError =>
                      setError(scanError instanceof Error ? scanError.message : String(scanError)),
                    );
                  }}
                >
                  {scanButtonLabel}
                </Button>
              </Box>
            </InfoCard>

            <Box mt={3}>
              <InfoCard title="Scan Summary">
                {!scanResults.length ? (
                  <Typography color="textSecondary">Run a scan to see file state.</Typography>
                ) : (
                  <>
                    {!scanIsCurrent ? (
                      <Alert severity="warning" style={{ marginBottom: 12 }}>
                        Selection, operation, or path changed since the last scan. Run scan again
                        before creating a PR job.
                      </Alert>
                    ) : null}
                    <Box mb={1}>
                      {Object.entries(scanSummary).map(([status, count]) => (
                        <Box key={status} mr={1} mb={1} component="span">
                          <Chip size="small" label={`${status}: ${count}`} />
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="body2">
                      Exact: {groupCount(scanGroups.exact)}. Normalized:{' '}
                      {groupCount(scanGroups.normalized)}.
                    </Typography>
                    <Typography variant="body2">
                      Largest exact group: {Math.max(0, ...exactGroups.map(group => group.length))}.
                    </Typography>
                    <Typography variant="body2">
                      Largest normalized group:{' '}
                      {Math.max(0, ...normalizedGroups.map(group => group.length))}.
                    </Typography>
                    <Box mt={2}>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={!scanIsCurrent || !eligibleScanResults.length}
                        onClick={() => setEditorOpen(true)}
                      >
                        {operation === 'create-missing-file'
                          ? `Compose file & create PRs (${eligibleScanResults.length})`
                          : `Open editor & create PRs (${eligibleScanResults.length})`}
                      </Button>
                    </Box>
                  </>
                )}
              </InfoCard>
            </Box>
          </Grid>
        </Grid>

        <Box mt={3}>
          <InfoCard title="SOC2 Production Property">
            <Typography variant="body2" color="textSecondary">
              Rule: repositories whose lifecycle is not <code>experimental</code> must have{' '}
              <code>soc2_production = true</code>; experimental repositories must not (unassigned or
              false). Lifecycle is read from the catalog; non-cataloged repos are treated as
              non-experimental.
            </Typography>
            <Box mt={2} mb={1}>
              <Button
                variant="outlined"
                disabled={!selectedRepos.length || soc2Busy}
                onClick={() => {
                  checkSoc2();
                }}
              >
                Check SOC2 ({selectedRepos.length})
              </Button>{' '}
              <Button
                color="primary"
                variant="contained"
                disabled={!selectedRepos.length || soc2Busy}
                onClick={() => {
                  fixSoc2();
                }}
              >
                Fix SOC2 ({selectedRepos.length})
              </Button>{' '}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={soc2OnlyFlagged}
                    onChange={event => setSoc2OnlyFlagged(event.target.checked)}
                  />
                }
                label="Show only non-compliant"
              />
            </Box>
            {soc2Results.length > 0 && !soc2IsCurrent && (
              <Alert severity="warning" style={{ marginBottom: 12 }}>
                Repository selection changed since the last SOC2 check. Run Check SOC2 again before
                relying on the table or Fix SOC2.
              </Alert>
            )}
            {displayedSoc2Results.length > 0 && (
              <Table
                options={{ paging: true, pageSize: 10, search: false }}
                columns={[
                  { title: 'Repository', render: (row: Soc2Result) => `${row.owner}/${row.repo}` },
                  {
                    title: 'Lifecycle',
                    field: 'lifecycle',
                    render: (row: Soc2Result) => row.lifecycle ?? '(none)',
                  },
                  { title: 'Current', field: 'current' },
                  { title: 'Desired', field: 'desired' },
                  {
                    title: 'Status',
                    render: (row: Soc2Result) =>
                      row.compliant ? (
                        <Chip size="small" color="primary" label="compliant" />
                      ) : (
                        <Chip size="small" label="needs fix" />
                      ),
                  },
                ]}
                data={
                  soc2OnlyFlagged
                    ? displayedSoc2Results.filter(result => !result.compliant)
                    : displayedSoc2Results
                }
              />
            )}
          </InfoCard>
        </Box>

        {displayedJob && (
          <Box mt={3}>
            <InfoCard title={`Job ${displayedJob.id}`}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1">{displayedJob.title}</Typography>
                  <Typography color="textSecondary">
                    Status: {displayedJob.status}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  {Object.entries(displayedJob.totals).map(([key, value]) => (
                    <Box key={key} mr={1} mb={1} component="span">
                      <Chip size="small" label={`${key}: ${value}`} />
                    </Box>
                  ))}
                </Grid>
              </Grid>
              <Table
                options={{ paging: true, pageSize: 10, search: false }}
                columns={targetColumns}
                data={displayedJob.targets}
              />
            </InfoCard>
          </Box>
        )}

        <Box mt={3}>
          <BatchJobsPanel
            jobs={jobsState.value ?? []}
            loading={jobsState.loading}
            error={jobsState.error}
            backendBaseUrl={backend.value}
            currentUserRef={currentUser.value}
            selectedJobId={currentJobId}
            onSelectJob={selectCurrentJob}
            onWorkflowChange={() => {
              setJobsPollTick(value => value + 1);
              setPollTick(value => value + 1);
            }}
          />
        </Box>

        <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Edit Content and Confirm</DialogTitle>
          <DialogContent>
            <Alert severity="info">
              This tool only opens pull requests on unique temporary branches. It will not force push,
              write directly to the default branch, or bypass branch protection. PRs are opened as
              your GitHub user (you may be asked to authorize GitHub), so you cannot approve your own
              pull requests.
            </Alert>
            <Box mt={2}>
              <Typography variant="subtitle1">
                Eligible targets: {eligibleScanResults.length}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {eligibleTargetsHelp}
              </Typography>
            </Box>
            <TextField
              fullWidth
              margin="normal"
              label={path}
              multiline
              minRows={18}
              variant="outlined"
              value={content}
              onChange={event => setContent(event.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={overwriteDiffering}
                  onChange={event => setOverwriteDiffering(event.target.checked)}
                />
              }
              label="I understand this may overwrite files that differ from the selected baseline, through PRs only."
            />
            <Divider />
            <Box mt={2}>
              <Typography variant="subtitle2">Preview baseline</Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography component="pre" style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                    {baseline ?? 'No existing file baseline. This is expected when creating missing files.'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!eligibleScanResults.length || submitting || !content}
              onClick={submitJob}
            >
              Create PR Job
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmUnsubscribeOpen}
          onClose={() => setConfirmUnsubscribeOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Catalog Unsubscribe</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              This removes Backstage catalog subscriptions for selected archived repositories only.
              It does not edit repository files, create commits, or open pull requests.
            </Alert>
            <Box mt={2}>
              <Typography variant="body2">
                Archived repositories selected: {selectedArchivedRepos.length}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmUnsubscribeOpen(false)}>Cancel</Button>
            <Button
              color="secondary"
              variant="contained"
              disabled={!selectedArchivedRepos.length || catalogBusy}
              onClick={() => {
                setConfirmUnsubscribeOpen(false);
                unregisterArchivedCatalogLocations();
              }}
            >
              Unsubscribe Archived Repos
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
}
